import React, { memo, useState, useCallback } from "react";
import LazyImage from "./LazyImage";
import NoImagePlaceholder from "./NoImagePlaceholder";
import { ColumnTypeIcon } from "../utils/columnUtils";
import { renderDataCellValue } from "../utils/renderUtils";
import { formatFieldValue, placeSheetFields, revealDelayStyle } from "../utils/productMode";

/**
 * Product mode's sheet layout: the table reflowed for a phone.
 *
 * One row per product — number, image, title with a second line beneath, and
 * a value on the right — under a sticky header that names the columns, so a
 * recording reads as a data sheet rather than a photo wall. Tapping a row
 * unfolds every field of that record; tapping its image opens the gallery.
 */

const ROW_GRID = "grid grid-cols-[2rem_3rem_minmax(0,1fr)_auto] items-center gap-x-3";

const ExternalLinkIcon = () => (
  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
  </svg>
);

// Type scale per slot: the title line, the muted line under it, the value on the right.
const SLOT_STYLES = {
  primary: {
    base: "text-[13px] font-medium leading-snug",
    text: (dark) => (dark ? "text-gray-100" : "text-gray-900"),
    price: (dark) => (dark ? "text-emerald-400" : "text-emerald-600"),
    empty: (dark) => (dark ? "text-gray-600" : "text-gray-400"),
  },
  secondary: {
    base: "text-[11px] leading-snug mt-0.5",
    text: () => "text-gray-500",
    price: (dark) => (dark ? "text-emerald-400/90" : "text-emerald-600"),
    empty: (dark) => (dark ? "text-gray-600" : "text-gray-400"),
  },
  trailing: {
    base: "text-[13px] font-semibold tabular-nums leading-snug",
    text: (dark) => (dark ? "text-gray-200" : "text-gray-700"),
    price: (dark) => (dark ? "text-emerald-400" : "text-emerald-600"),
    empty: (dark) => (dark ? "text-gray-600" : "text-gray-400"),
  },
};

const FieldText = ({ value, column, slot, isDarkMode }) => {
  const { text, kind, href } = formatFieldValue(value, column);
  const styles = SLOT_STYLES[slot];

  if (kind === "link") {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(e) => e.stopPropagation()}
        className={`inline-flex items-center gap-1 max-w-full ${styles.base} ${
          isDarkMode ? "text-blue-400 hover:text-blue-300" : "text-blue-600 hover:text-blue-700"
        }`}
      >
        <span className="truncate">{text}</span>
        <span className={isDarkMode ? "text-gray-500" : "text-gray-400"}>
          <ExternalLinkIcon />
        </span>
      </a>
    );
  }

  return (
    <span className={`block truncate ${styles.base} ${styles[kind](isDarkMode)}`} title={kind === "empty" ? undefined : text}>
      {text}
    </span>
  );
};

const SheetHeader = ({ placement, isDarkMode }) => {
  const { primary, secondary, trailing } = placement;
  return (
    <div
      className={`${ROW_GRID} sticky top-0 z-10 px-3 py-2 text-[10px] font-medium uppercase tracking-wider border-b ${
        isDarkMode ? "bg-[#0a0a0b] border-white/[0.06] text-gray-400" : "bg-[#fafafa] border-gray-200/80 text-gray-500"
      }`}
    >
      <span>#</span>
      <span>Image</span>
      <span className="flex items-center gap-1.5 min-w-0">
        {primary ? (
          <>
            <ColumnTypeIcon type={primary.type} isDarkMode={isDarkMode} />
            <span className="truncate">{primary.name || primary.id}</span>
          </>
        ) : (
          <span>Product</span>
        )}
        {secondary && <span className="truncate opacity-60">· {secondary.name || secondary.id}</span>}
      </span>
      <span className="flex items-center justify-end gap-1.5 min-w-0">
        {trailing && (
          <>
            <ColumnTypeIcon type={trailing.type} isDarkMode={isDarkMode} />
            <span className="truncate">{trailing.name || trailing.id}</span>
          </>
        )}
      </span>
    </div>
  );
};

// Every field of one row, rendered the way the table renders its cells.
const RecordPanel = ({ row, columns, isDarkMode, onOpenGallery }) => (
  <div
    className={`px-3 pb-3 pt-1 border-b animate-in fade-in slide-in-from-top-1 duration-200 ${
      isDarkMode ? "border-white/[0.04] bg-white/[0.02]" : "border-gray-100 bg-gray-50/60"
    }`}
  >
    <dl
      className={`grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] gap-x-3 gap-y-2.5 rounded-lg border p-3 ${
        isDarkMode ? "bg-[#111113] border-white/[0.06]" : "bg-white border-gray-200"
      }`}
    >
      {columns.map((column) => (
        <React.Fragment key={column.id}>
          <dt className="flex items-start gap-1.5 min-w-0 pt-0.5 text-[10px] font-medium uppercase tracking-wider leading-tight text-gray-500">
            <span className="shrink-0">
              <ColumnTypeIcon type={column.type} isDarkMode={isDarkMode} />
            </span>
            <span className="min-w-0 break-words">{column.name || column.id}</span>
          </dt>
          <dd className="min-w-0 text-xs break-words">
            {renderDataCellValue(row, column.id, column.type, column, columns, isDarkMode, (images, initialIndex) =>
              onOpenGallery(row, images[initialIndex])
            )}
          </dd>
        </React.Fragment>
      ))}
    </dl>
  </div>
);

const SheetRow = memo(
  ({ row, image, index, placement, fit, recordColumns, isDarkMode, selected, expanded, onToggle, onOpenGallery }) => {
    const { primary, secondary, trailing } = placement;
    const hasImage = Boolean(image.url);

    const handleKeyDown = (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onToggle(row.id);
      }
    };

    return (
      <div
        data-row-id={row.id}
        className={`animate-list-item-in motion-reduce:animate-none scroll-mt-9 ${selected ? "table-row-selected" : ""}`}
        style={revealDelayStyle(index)}
      >
        <div
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          onClick={() => onToggle(row.id)}
          onKeyDown={handleKeyDown}
          className={`${ROW_GRID} px-3 py-2 border-b cursor-pointer select-none transition-colors duration-100 ${
            isDarkMode ? "border-white/[0.04]" : "border-gray-100"
          } ${
            expanded
              ? isDarkMode
                ? "bg-white/[0.03]"
                : "bg-gray-50"
              : isDarkMode
              ? "hover:bg-white/[0.02]"
              : "hover:bg-gray-50/70"
          }`}
          style={{ WebkitTapHighlightColor: "transparent" }}
        >
          <span className={`text-[11px] tabular-nums ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
            {row.displayIndex ?? row.id}
          </span>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              if (hasImage) onOpenGallery(row);
            }}
            disabled={!hasImage}
            aria-label={hasImage ? "Open images" : "No images"}
            className={`relative h-12 w-12 rounded-md overflow-hidden ring-1 transition-transform duration-150 ${
              hasImage ? "active:scale-95" : "cursor-default"
            } ${isDarkMode ? "ring-white/10" : "ring-gray-200"} ${
              fit === "contain" ? "bg-white" : isDarkMode ? "bg-white/[0.04]" : "bg-gray-100"
            }`}
          >
            {hasImage ? (
              // Keyed by URL so a changed image source starts from a clean load state.
              <LazyImage key={image.url} url={image.url} alt="" className="relative w-full h-full" isDarkMode={isDarkMode} fit={fit} />
            ) : (
              <NoImagePlaceholder isDarkMode={isDarkMode} iconClassName="w-4 h-4" />
            )}
          </button>
          <div className="min-w-0">
            {primary && <FieldText value={row.data?.[primary.id]} column={primary} slot="primary" isDarkMode={isDarkMode} />}
            {secondary && <FieldText value={row.data?.[secondary.id]} column={secondary} slot="secondary" isDarkMode={isDarkMode} />}
          </div>
          <div className="min-w-0 max-w-[7.5rem] text-right">
            {trailing && <FieldText value={row.data?.[trailing.id]} column={trailing} slot="trailing" isDarkMode={isDarkMode} />}
          </div>
        </div>
        {expanded && <RecordPanel row={row} columns={recordColumns} isDarkMode={isDarkMode} onOpenGallery={onOpenGallery} />}
      </div>
    );
  }
);
SheetRow.displayName = "SheetRow";

const ProductSheet = ({ items, fields, priceColumnId, recordColumns, settings, isDarkMode, selectedRowId, onOpenGallery }) => {
  // One record open at a time keeps the sheet readable while filming.
  const [expandedRowId, setExpandedRowId] = useState(null);
  const toggleRow = useCallback((rowId) => setExpandedRowId((prev) => (prev === rowId ? null : rowId)), []);
  const placement = placeSheetFields(fields, priceColumnId);

  return (
    <div className="pb-6">
      <SheetHeader placement={placement} isDarkMode={isDarkMode} />
      {items.map(({ row, image }, index) => (
        <SheetRow
          key={row.id}
          row={row}
          image={image}
          index={index}
          placement={placement}
          fit={settings.imageFit}
          recordColumns={recordColumns}
          isDarkMode={isDarkMode}
          selected={selectedRowId === row.id}
          expanded={expandedRowId === row.id}
          onToggle={toggleRow}
          onOpenGallery={onOpenGallery}
        />
      ))}
    </div>
  );
};

export default ProductSheet;
